import { useState } from 'react';
import { adminApi } from '../lib/admin-api';
import toast from 'react-hot-toast';

/**
 * Unified hook for handling delete operations in admin sections
 * Provides consistent delete behavior with confirmation, API calls, and notifications
 *
 * @param {Object} config - Configuration object
 * @param {string} config.resourceType - Type of resource (user, role, product, etc.)
 * @param {Function} config.onSuccess - Callback after successful deletion
 * @param {Function} config.getItemName - Function to extract display name from item
 * @param {Function} config.getConfirmMessage - Optional custom confirmation message
 * @param {boolean} config.skipConfirmation - Skip confirmation dialog by default
 * @returns {Object} Delete handler and loading state
 */
export function useAdminDelete({
  resourceType,
  onSuccess,
  getItemName = (item) => item?.name || item?.username || 'Item',
  getConfirmMessage = null,
  skipConfirmation = false
}) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  // Map resource types to API methods
  const apiMethods = {
    user: adminApi.users.delete,
    role: adminApi.roles.delete,
    product: adminApi.products.delete,
    invitation: adminApi.invitations.delete,
    configuration: adminApi.modelConfigurations.delete,
    ai: adminApi.modelConfigurations.delete,
  };

  /**
   * Handle delete with confirmation
   * @param {Object|string} itemOrId - Item object or ID to delete
   * @param {Object} options - Additional options
   * @param {string} options.customMessage - Custom confirmation message
   * @param {boolean} options.skipConfirm - Skip confirmation dialog
   * @returns {Promise<boolean>} Success status
   */
  const handleDelete = async (itemOrId, options = {}) => {
    // Extract ID and item
    const itemId = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id;
    const item = typeof itemOrId === 'object' ? itemOrId : null;

    if (!itemId) {
      console.error('No item ID provided for deletion');
      return false;
    }

    // Get item name for messages
    const itemName = item ? getItemName(item) : 'this item';

    // Confirmation message
    const confirmMessage = options.customMessage ||
      getConfirmMessage?.(item, itemName) ||
      `Are you sure you want to delete ${itemName}? This action cannot be undone.`;

    // Confirm deletion (unless skipped globally or per-call)
    const shouldSkipConfirm = skipConfirmation || options.skipConfirm;
    if (!shouldSkipConfirm) {
      // Show modal instead of browser confirm
      setPendingDelete({ itemId, item, itemName, confirmMessage, options });
      setShowDeleteModal(true);
      return false; // Will be handled by confirmDelete
    }

    // Perform delete directly if confirmation is skipped
    return await performDelete(itemId, item, itemName);
  };

  /**
   * Perform the actual delete operation
   * @param {string} itemId - ID of item to delete
   * @param {Object} item - Item object
   * @param {string} itemName - Display name of item
   * @returns {Promise<boolean>} Success status
   */
  const performDelete = async (itemId, item, itemName) => {
    // Get API method
    const deleteMethod = apiMethods[resourceType];
    if (!deleteMethod) {
      console.error(`Unknown resource type: ${resourceType}`);
      toast.error('Delete operation not supported');
      return false;
    }

    try {
      setIsDeleting(true);

      // Call delete API
      await deleteMethod(itemId);

      // Success notification
      toast.success(`${itemName} deleted successfully`);

      // Callback for refresh/cleanup
      if (onSuccess) {
        await onSuccess(itemId, item);
      }

      return true;
    } catch (error) {
      console.error(`Failed to delete ${resourceType}:`, error);
      toast.error(error.message || `Failed to delete ${itemName}`);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Confirm and execute pending delete
   */
  const confirmDelete = async () => {
    if (!pendingDelete) return;

    const { itemId, item, itemName } = pendingDelete;
    setShowDeleteModal(false);
    setPendingDelete(null);

    await performDelete(itemId, item, itemName);
  };

  /**
   * Cancel pending delete
   */
  const cancelDelete = () => {
    setShowDeleteModal(false);
    setPendingDelete(null);
  };

  return {
    handleDelete,
    isDeleting,
    // Modal state and handlers
    deleteModal: {
      isOpen: showDeleteModal,
      onConfirm: confirmDelete,
      onCancel: cancelDelete,
      title: 'Confirm Deletion',
      message: pendingDelete?.confirmMessage,
      itemName: pendingDelete?.itemName
    }
  };
}
